import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { ContractListResponse } from '@/pages/contracts/types';

interface GoToReviewButtonProps {
  /** Completed pipeline job whose linked contract we navigate to. */
  jobId: string;
}

/**
 * Resolves the contract created by the pipeline for this job and renders a
 * prominent "Go to review" CTA linking to the Contract Detail review view.
 *
 * The contract is synced best-effort at pipeline completion
 * (sync_contract_from_pipeline), so if none is found yet we render nothing
 * rather than a dead link.
 */
export function GoToReviewButton({ jobId }: GoToReviewButtonProps) {
  const [contractId, setContractId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ContractListResponse>(`/api/v1/contracts?job_id=${jobId}&limit=1`)
      .then((res) => {
        if (!cancelled) setContractId(res.data[0]?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setContractId(null);
      });
    return () => { cancelled = true; };
  }, [jobId]);

  if (!contractId) return null;

  return (
    <Button asChild size="sm" className="gap-1.5">
      <Link to={`/contracts/${contractId}`} aria-label="Go to contract review">
        Go to review
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </Button>
  );
}
